import React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'
import difference from 'lodash/difference'
import groupBy from 'lodash/groupBy'
import intersection from 'lodash/intersection'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'

import * as modalAction from '../../../actions/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import useDispatch from '../../../hooks/use-dispatch'
import useSelector from '../../../hooks/use-selector'
import * as shippingApi from '../../../services/api/shipping'
import {formDataToShippingRequests} from '../../forms/shippings/utils'
import {toValidationSchema} from '../../utils/form'

import {Country, CountryCode, ModalMeta} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import LabeledCheckbox from '../../../../../commons/src/components/labeled-checkbox'
import LinkButton from '../../../../../commons/src/components/link-button'
import Modal from '../../../../../commons/src/components/modal'
import CheckboxTreeLayout from '../../../components/checkbox-tree-layout'

import ShippingsForm, {
  SingleFormData,
  initialValues,
  validationSchema,
} from '../../forms/shippings/form'

type Props = {
  meta: ModalMeta
  onDone?: () => void
}

const toggleCountryCodes = (
  countryCodes: CountryCode[],
  countryCodesOfConcern: CountryCode[],
  value: boolean
) => {
  if (value) {
    return uniq([...countryCodes, ...countryCodesOfConcern])
  }

  return difference(countryCodes, countryCodesOfConcern)
}

const CreateShippingsModal = ({meta, onDone = noop}: Props) => {
  const dispatch = useDispatch()
  const provider = useSelector(state => state.core.provider)
  const countries = useSelector(state => state.core.countries)

  invariant(provider, 'Provider missing.')
  invariant(countries, 'Countries missing.')

  const [showRegion, setShowRegion] = React.useState<Record<string, boolean>>({})

  const [regions, countriesWithoutRegion] = React.useMemo(
    () => [
      groupBy(
        Object.values(countries).filter(country => country.region),
        'region'
      ),
      sortBy(
        Object.values(countries).filter(country => !country.region),
        'name'
      ),
    ],
    Object.values(countries)
  )

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const requests = formDataToShippingRequests(values, provider)

      try {
        await shippingApi.createMultiple(requests)

        onDone()
        dispatch(modalAction.close())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const renderCountry = (country: Country) => (
    <LabeledCheckbox
      key={country.code}
      tiny
      baseline
      name="countryCodes"
      label={country.name}
      checked={formik.values.countryCodes.includes(country.code)}
      error={formik.submitCount > 0 && !!formik.errors.countryCodes}
      onChange={(value, name) =>
        formik.setFieldValue(
          name || '',
          toggleCountryCodes(formik.values.countryCodes, [country.code], value),
          true
        )
      }
      onBlur={formik.handleBlur}
    />
  )

  const renderRegion = (region: string) => {
    const countriesOfRegion = sortBy(regions[region], 'name')
    const countryCodes = countriesOfRegion.map(country => country.code)
    const childrenVisible = showRegion[region]
    const numSelectedCountries = intersection(countryCodes, formik.values.countryCodes).length

    return (
      <CheckboxTreeLayout
        key={region}
        header={
          <LabeledCheckbox
            tiny
            baseline
            name="countryCodes"
            label={region}
            count={`${numSelectedCountries}/${countriesOfRegion.length}`}
            suffix={
              <LinkButton
                onClick={() => setShowRegion({...showRegion, [region]: !childrenVisible})}
              >
                {childrenVisible ? 'Hide all' : 'Show all'}
              </LinkButton>
            }
            error={formik.submitCount > 0 && !!formik.errors.countryCodes}
            checked={numSelectedCountries > 0}
            partial={numSelectedCountries < countriesOfRegion.length}
            onChange={(value, name) =>
              formik.setFieldValue(
                name || '',
                toggleCountryCodes(
                  formik.values.countryCodes,
                  countryCodes,
                  numSelectedCountries < countriesOfRegion.length || value
                ),
                true
              )
            }
            onBlur={formik.handleBlur}
          />
        }
      >
        {childrenVisible && countriesOfRegion.map(renderCountry)}
      </CheckboxTreeLayout>
    )
  }

  const renderRegions = () => {
    const regionsList = Object.keys(regions).sort()

    return (
      <FormRow layout="half-half">
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.countryCodes}>
          {regionsList.slice(0, Math.ceil(regionsList.length / 2)).map(renderRegion)}
        </FieldNote>
        <div>
          {regionsList.slice(Math.ceil(regionsList.length / 2)).map(renderRegion)}
          {countriesWithoutRegion.map(renderCountry)}
        </div>
      </FormRow>
    )
  }

  return (
    <Modal
      size="l"
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Create new shipping option" size="l" />}
      onClose={() => dispatch(modalAction.close())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(modalAction.close())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <ShippingsForm formik={formik} provider={provider}>
        <Headline size="s" label="Regions" classNames={['u-margin-top-l']} />
        {renderRegions()}
      </ShippingsForm>
    </Modal>
  )
}

export default CreateShippingsModal
